"use client";

import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ForgotPassword, ForgotPasswordSchema } from "@/lib/validations";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { ArrowLeft } from "lucide-react";
import { cn } from "@/lib/utils";
import { ForgotPasswordFormProps } from "@/components/auth/types";

export function ForgotPasswordForm({
  onSubmit,
  status,
  onViewChange,
  defaultValues,
}: ForgotPasswordFormProps) {
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [countdown, setCountdown] = useState(0);

  const form = useForm<ForgotPassword>({
    resolver: zodResolver(ForgotPasswordSchema),
    defaultValues: defaultValues || {
      email: "",
    },
  });

  const startCountdown = () => {
    setCountdown(30);
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleSubmit = async (values: ForgotPassword) => {
    await onSubmit(values);
    setShowConfirmation(true);
    startCountdown();
  };

  return (
    <div className='space-y-4'>
      {showConfirmation ? (
        <div className='space-y-4'>
          <p className='text-sm text-muted-foreground'>
            Check your email to get new password link. If you did not receive an
            email, your account may not exist.
          </p>
          {countdown > 0 ? (
            <p className='text-sm'>You can try again in {countdown} seconds</p>
          ) : (
            <Button
              onClick={() => setShowConfirmation(false)}
              className='w-full'
            >
              Try Again
            </Button>
          )}
        </div>
      ) : (
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className='space-y-4'
          >
            <FormField
              control={form.control}
              name='email'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder='Enter your email'
                      type='email'
                      disabled={status === "executing"}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button
              type='submit'
              className={cn(
                "w-full",
                status === "executing" ? "animate-pulse" : ""
              )}
              disabled={status === "executing" || countdown > 0}
            >
              {countdown > 0
                ? `Try again in ${countdown}s`
                : status === "executing"
                ? "Sending..."
                : "Send Reset Link"}
            </Button>
          </form>
        </Form>
      )}
      <p className='text-sm text-muted-foreground mt-2'>
        We&apos;ll send you an email with instructions to reset your password.
      </p>
      <div className='flex justify-end'>
        <Button
          variant='link'
          className='p-0 h-auto font-normal'
          onClick={() => onViewChange?.("login")}
          type='button'
        >
          <ArrowLeft className='h-4 w-4 mr-2' />
          Back to Login
        </Button>
      </div>
    </div>
  );
}
